import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useReducedMotion } from "framer-motion";
import { api } from "../lib/api";
import { useSeo } from "../hooks/useSeo";
import { KenBurns } from "../components/motion/KenBurns";
import { WordReveal } from "../components/motion/WordReveal";
import { RecentShows } from "../components/home/RecentShows";
import { LatestEditionBanner } from "../components/home/LatestEdition";
import { CircledArrow, Roundel, SunIcon } from "../components/brand/icons";

const FALLBACK_SUBTITLE = "Pantheon Art is proud to manage *Johnny Depp's visual art* since 2021.";

const LinkRow = ({ to, label, testid }) => (
  <Link
    to={to}
    data-testid={testid}
    className="group hairline-t flex items-center justify-between px-5 py-8 transition-colors duration-500 hover:bg-stone-40 focus-visible:bg-stone-40 lg:px-10 lg:py-10"
  >
    <span className="h-condensed text-3xl transition-transform duration-500 group-hover:translate-x-3 sm:text-4xl lg:text-5xl">
      {label}
    </span>
    <CircledArrow className="h-8 w-8 transition-transform duration-500 group-hover:translate-x-2 lg:h-10 lg:w-10" />
  </Link>
);

export default function HomePage() {
  const [home, setHome] = useState(null);
  const [drawn, setDrawn] = useState(false);
  const reduce = useReducedMotion();
  const saveData = typeof navigator !== "undefined" && navigator.connection && navigator.connection.saveData;

  useEffect(() => {
    api
      .get("/settings/home")
      .then((res) => setHome(res.data))
      .catch(() => {});
  }, []);

  const heroImage = home?.hero_image || null;
  const heroPoster = home?.hero_poster_image || heroImage;
  const heroIsVideo =
    !reduce && !saveData && home?.hero_media_type === "video" && (home?.hero_desktop_video || home?.hero_mobile_video);

  useEffect(() => {
    if (!heroPoster) return undefined;
    const link = document.createElement("link");
    link.rel = "preload";
    link.as = "image";
    link.href = heroPoster;
    document.head.appendChild(link);
    return () => link.remove();
  }, [heroPoster]);

  useSeo({
    title: "Pantheon Art — Art Management, Publishing and Studio",
    description: "Pantheon Art is an art management agency, publisher and studio for high profile artists, proudly managing Johnny Depp's visual art since 2021.",
    image: heroPoster,
    path: "/",
    jsonLd: [
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Pantheon Art",
        url: window.location.origin,
        logo: `${window.location.origin}/assets/logos/roundel-black.webp`,
        address: "48 Curzon Street, London W1J 7UL, UK",
      },
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "Pantheon Art",
        url: window.location.origin,
      },
    ],
  });

  return (
    <div data-testid="home-page">
      <section data-hero data-testid="home-hero" className="relative flex h-[100svh] items-center justify-center overflow-hidden bg-charcoal">
        {heroIsVideo ? (
          <video
            data-testid="hero-background-video"
            className="absolute inset-0 h-full w-full object-cover"
            autoPlay
            muted
            loop
            playsInline
            poster={heroPoster}
          >
            {home?.hero_desktop_video && <source src={home.hero_desktop_video} media="(min-width: 768px)" />}
            {home?.hero_mobile_video && <source src={home.hero_mobile_video} media="(max-width: 767px)" />}
          </video>
        ) : heroImage ? (
          <KenBurns src={home?.hero_media_type === "video" ? heroPoster : heroImage} alt={home?.hero_alt || ""} className="absolute inset-0" imgClassName="opacity-90" />
        ) : null}
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/70 via-charcoal/20 to-charcoal/30" aria-hidden="true" />
        {reduce || saveData ? (
          <Roundel variant="offwhite" className="relative z-10 w-[31vmin] max-w-[170px]" />
        ) : (
          <video
            data-testid="hero-logo-animation"
            className={`relative z-10 w-[31vmin] max-w-[170px] mix-blend-screen ${drawn ? "hero-rotate" : ""}`}
            poster="/assets/logos/logo-poster.jpg"
            autoPlay
            muted
            playsInline
            preload="auto"
            onEnded={() => setDrawn(true)}
            aria-label="Pantheon Art"
          >
            <source src="/assets/logos/logo-animation.webm" type="video/webm" />
            <source src="/assets/logos/logo-animation.mp4" type="video/mp4" />
          </video>
        )}
        <div className="absolute bottom-8 left-1/2 z-10 flex -translate-x-1/2 flex-col items-center gap-3 text-offwhite" data-testid="scroll-cue">
          <span className="scroll-cue-line block h-12 w-px bg-offwhite" />
          <SunIcon variant="offwhite" className="h-5 w-5" />
        </div>
      </section>

      <section className="px-5 py-24 lg:px-10 lg:py-36" data-testid="home-subtitle-section">
        <WordReveal
          text={home?.scroll_subtitle || FALLBACK_SUBTITLE}
          className="h-long max-w-[26ch] text-[clamp(1.75rem,4.2vw,3.4rem)]"
        />
      </section>

      <RecentShows />

      <LatestEditionBanner />

      <section className="hairline-b" data-testid="home-links-section">
        <LinkRow to="/johnny-depp/cv" label="Artist CV" testid="home-row-artist-cv" />
        <LinkRow to="/press" label="Recent Press" testid="home-row-recent-press" />
        <LinkRow to="/contact" label="Enquiries" testid="home-row-enquiries" />
      </section>
    </div>
  );
}
